import {
  forwardRef,
  type ElementType,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "./utils/cn";

export type SurfaceElevation = "flat" | "raised";
export type SurfacePadding = "none" | "sm" | "md";

export interface SurfaceProps extends HTMLAttributes<HTMLElement> {
  as?: ElementType;
  /** `flat` = gradient hairline + soft shadow; `raised` = same hairline + a deeper shadow. */
  elevation?: SurfaceElevation;
  padding?: SurfacePadding;
  children?: ReactNode;
}

const elevationClasses: Record<SurfaceElevation, string> = {
  flat: "card-premium",
  raised: "card-premium card-premium--raised",
};

const paddingClasses: Record<SurfacePadding, string> = {
  none: "",
  sm: "p-3",
  md: "p-5",
};

/**
 * Canonical card / section container. Replaces ~11 ad-hoc card class
 * combinations. `bg-surface` + the semantic border token dark-mode for free.
 */
const Surface = forwardRef<HTMLElement, SurfaceProps>(
  (
    { as, elevation = "flat", padding = "md", className, children, ...rest },
    ref
  ) => {
    const Component = (as || "div") as ElementType;
    return (
      <Component
        ref={ref}
        className={cn(
          "rounded-card bg-surface text-menu",
          elevationClasses[elevation],
          paddingClasses[padding],
          className
        )}
        {...rest}
      >
        {children}
      </Component>
    );
  }
);

Surface.displayName = "Surface";

export default Surface;
